/**
 * AdversarialEngine — deterministic stress-testing and attack simulation
 * for Mist contracts based on their compiled PVI graph.
 *
 * Generates structured, explainable adversarial scenarios, executes them
 * against the real SimulationEngine, and classifies failures.
 *
 * This is NOT random fuzzing — every scenario is deterministic and reproducible.
 */

import type { NodeId, PviGraphWithSpans, Condition } from './pviTypes';
import {
  runSimulation,
  deriveInputs,
  oracleDefaultForOp,
  type SimulationInputValues,
  type SimulationResult,
  type ExecutionStep,
} from './simulationEngine';

/* ================================================================== */
/*  Scenario Types                                                    */
/* ================================================================== */

export type ScenarioCategory =
  | 'boundary'
  | 'balance_attack'
  | 'split_integrity'
  | 'conditional_logic'
  | 'flow_integrity'
  | 'multi_step_stress';

export const SCENARIO_CATEGORIES: {
  id: ScenarioCategory;
  label: string;
  description: string;
}[] = [
  { id: 'boundary', label: 'Boundary Conditions', description: 'Zero amounts, extremely large amounts, min/max values' },
  { id: 'balance_attack', label: 'Balance Attacks', description: 'Insufficient balance, partial funding, depleted escrow' },
  { id: 'split_integrity', label: 'Split Integrity', description: 'Invalid split totals, rounding edge cases, overflow' },
  { id: 'conditional_logic', label: 'Conditional Logic', description: 'Force false conditions, edge states, unexpected branches' },
  { id: 'flow_integrity', label: 'Flow Integrity', description: 'Unreachable nodes, dangling HOLDs, missing refund paths' },
  { id: 'multi_step_stress', label: 'Multi-Step Stress', description: 'Repeated execution, chained operations, rapid scenarios' },
];

/* ================================================================== */
/*  Failure Types                                                     */
/* ================================================================== */

export type FailureType =
  | 'insufficient_balance'
  | 'invalid_flow'
  | 'failed_condition'
  | 'value_mismatch'
  | 'split_inconsistency'
  | 'unreachable_execution'
  | 'incomplete_flow'
  | 'overflow'
  | 'rounding_error';

export interface AdversarialFailure {
  type: FailureType;
  nodeId: NodeId | null;
  explanation: string;
  suggestedFix: string | null;
}

/* ================================================================== */
/*  Run + Result Types                                                */
/* ================================================================== */

export interface AdversarialRun {
  id: number;
  label: string;
  inputs: SimulationInputValues;
  result: SimulationResult;
  failures: AdversarialFailure[];
  /** Subset of steps that are relevant to the failure path. */
  executionTrace: ExecutionStep[];
}

export interface ScenarioResult {
  category: ScenarioCategory;
  label: string;
  runs: AdversarialRun[];
  totalRuns: number;
  totalFailures: number;
  totalWarnings: number;
}

export interface AdversarialResult {
  scenarios: ScenarioResult[];
  totalRuns: number;
  totalFailures: number;
  totalWarnings: number;
  /** Node IDs that were involved in any failure. */
  failedNodeIds: Set<NodeId>;
  /** Node IDs that had warnings. */
  warningNodeIds: Set<NodeId>;
  /** Node IDs that passed all scenarios. */
  passedNodeIds: Set<NodeId>;
  timestamp: number;
}

/* ================================================================== */
/*  Static Analysis Helpers                                           */
/* ================================================================== */

function findUnreachableNodes(graph: PviGraphWithSpans): NodeId[] {
  if (graph.nodes.length === 0) return [];

  const hasIncoming = new Set<NodeId>();
  const sources = new Set<NodeId>(graph.nodes.map(n => n.id));

  for (const e of graph.edges) {
    hasIncoming.add(e.destination);
  }

  // Root nodes (no incoming edges)
  const roots = graph.nodes.filter(n => !hasIncoming.has(n.id)).map(n => n.id);
  if (roots.length === 0) return [...sources]; // cycle — all are unreachable from outside

  // BFS from roots
  const reachable = new Set<NodeId>();
  const queue = [...roots];
  while (queue.length > 0) {
    const nid = queue.shift()!;
    if (reachable.has(nid)) continue;
    reachable.add(nid);
    for (const e of graph.edges) {
      if (e.source === nid && !reachable.has(e.destination)) {
        queue.push(e.destination);
      }
    }
  }

  return graph.nodes.filter(n => !reachable.has(n.id)).map(n => n.id);
}

function findDanglingHolds(graph: PviGraphWithSpans): NodeId[] {
  const holdIds = graph.nodes
    .filter(n => n.nodeType === 'HOLD')
    .map(n => n.id);

  const holdWithOutgoing = new Set<NodeId>();
  for (const e of graph.edges) {
    if (holdIds.includes(e.source)) holdWithOutgoing.add(e.source);
  }

  return holdIds.filter(id => !holdWithOutgoing.has(id));
}

function findMissingRefundPaths(graph: PviGraphWithSpans): NodeId[] {
  const holdIds = new Set(graph.nodes.filter(n => n.nodeType === 'HOLD').map(n => n.id));

  // HOLD nodes that have NO path to a REFUND, RELEASE, or 2+ PAYs
  const holdsWithReturnPath = new Set<NodeId>();
  for (const holdId of holdIds) {
    let payCount = 0;
    const visited = new Set<NodeId>();
    const q = [holdId];
    let found = false;
    while (q.length > 0) {
      const cur = q.shift()!;
      if (visited.has(cur)) continue;
      visited.add(cur);
      const node = graph.nodes.find(n => n.id === cur);
      if (node && cur !== holdId) {
        if (node.nodeType === 'REFUND' || node.nodeType === 'RELEASE') {
          holdsWithReturnPath.add(holdId);
          found = true;
          break;
        }
        if (node.nodeType === 'PAY') payCount++;
      }
      for (const e of graph.edges) {
        if (e.source === cur) q.push(e.destination);
      }
    }
    // If there are 2+ PAY paths downstream (release + cancel), that's sufficient
    if (!found && payCount >= 2) holdsWithReturnPath.add(holdId);
  }

  return [...holdIds].filter(id => !holdsWithReturnPath.has(id));
}

/**
 * Identify HOLD nodes that have multiple drain operations downstream
 * (e.g. both RELEASE and REFUND in separate functions).
 * Returns pairs of { holdId, drainNodeIds, drainTypes }.
 */
interface MultiDrainVault {
  holdId: NodeId;
  drainNodeIds: NodeId[];
  drainTypes: string[];
}

function findMultiDrainVaults(graph: PviGraphWithSpans): MultiDrainVault[] {
  const holdIds = graph.nodes.filter(n => n.nodeType === 'HOLD').map(n => n.id);
  const results: MultiDrainVault[] = [];

  for (const holdId of holdIds) {
    const drainNodeIds: NodeId[] = [];
    const drainTypes: string[] = [];
    const visited = new Set<NodeId>();
    const q = [holdId];

    while (q.length > 0) {
      const cur = q.shift()!;
      if (visited.has(cur)) continue;
      visited.add(cur);
      const node = graph.nodes.find(n => n.id === cur);
      if (node && cur !== holdId) {
        if (node.nodeType === 'RELEASE' || node.nodeType === 'REFUND' || node.nodeType === 'PAY') {
          drainNodeIds.push(cur);
          drainTypes.push(node.nodeType);
        }
      }
      for (const e of graph.edges) {
        if (e.source === cur) q.push(e.destination);
      }
    }

    if (drainNodeIds.length >= 2) {
      results.push({ holdId, drainNodeIds, drainTypes });
    }
  }

  return results;
}

/**
 * Check whether two sets of conditions are mutually exclusive —
 * i.e. they can never both be true at the same time.
 *
 * Detects complementary operators on the same oracle/field or time threshold.
 * e.g. `oracle BTC_USD.price > 50000` vs `oracle BTC_USD.price <= 50000`
 */
function areMutuallyExclusive(a: Condition[], b: Condition[]): boolean {
  if (a.length === 0 || b.length === 0) return false;

  for (const ca of a) {
    for (const cb of b) {
      if (ca.type === 'oracle_threshold' && cb.type === 'oracle_threshold') {
        if (ca.oracle_id === cb.oracle_id && ca.field === cb.field && ca.value === cb.value) {
          if (areComplementaryOps(ca.operator, cb.operator)) return true;
        }
      }
      if (ca.type === 'time_absolute' && cb.type === 'time_absolute') {
        // One requires >= height, the other requires < height
        if (ca.after_height === cb.after_height) return true;
      }
    }
  }
  return false;
}

function areComplementaryOps(a: string, b: string): boolean {
  const pairs: [string, string][] = [
    ['gt', 'le'], ['le', 'gt'],
    ['ge', 'lt'], ['lt', 'ge'],
    ['eq', 'ne'], ['ne', 'eq'],
  ];
  return pairs.some(([x, y]) => a === x && b === y);
}

function checkLockedValue(result: SimulationResult): number {
  const escrow = result.finalBalances['__escrow__'];
  if (!escrow) return 0;
  return Object.values(escrow).reduce((s, v) => s + v, 0);
}

/* ================================================================== */
/*  Scenario Generators                                               */
/* ================================================================== */

function getBaseInputs(graph: PviGraphWithSpans): SimulationInputValues {
  const inputs = deriveInputs(graph);
  const values: SimulationInputValues = {};
  for (const inp of inputs) {
    values[inp.name] = inp.defaultValue;
  }
  return values;
}

function getAmountKeys(graph: PviGraphWithSpans): string[] {
  const inputs = deriveInputs(graph);
  return inputs.filter(i => i.type === 'number' && i.name.startsWith('initial_balance')).map(i => i.name);
}

function generateBoundaryScenario(graph: PviGraphWithSpans): AdversarialRun[] {
  const base = getBaseInputs(graph);
  const amountKeys = getAmountKeys(graph);
  if (amountKeys.length === 0) {
    // Fallback: add a generic key
    amountKeys.push('initial_balance');
  }

  const runs: AdversarialRun[] = [];
  let runId = 0;

  // Zero amounts
  for (const key of amountKeys) {
    const inputs = { ...base, [key]: '0' };
    runs.push(makeRun(runId++, `Zero ${key}`, inputs, graph));
  }

  // One unit (minimum)
  for (const key of amountKeys) {
    const inputs = { ...base, [key]: '1' };
    runs.push(makeRun(runId++, `Minimum ${key} = 1`, inputs, graph));
  }

  // Very large amounts
  for (const key of amountKeys) {
    const inputs = { ...base, [key]: '999999999999' };
    runs.push(makeRun(runId++, `Very large ${key}`, inputs, graph));
  }

  // Exact amount (just enough to cover HOLD)
  const holdNodes = graph.nodes.filter(n => n.nodeType === 'HOLD');
  if (holdNodes.length > 0) {
    const exactAmount = holdNodes.reduce((s, n) => {
      const a = parseFloat(n.amount);
      return s + (isNaN(a) ? 1000000 : a);
    }, 0);
    for (const key of amountKeys) {
      const inputs = { ...base, [key]: String(exactAmount) };
      runs.push(makeRun(runId++, `Exact amount ${key} = ${exactAmount}`, inputs, graph));
    }
  }

  return runs;
}

function generateBalanceAttackScenario(graph: PviGraphWithSpans): AdversarialRun[] {
  const base = getBaseInputs(graph);
  const amountKeys = getAmountKeys(graph);
  if (amountKeys.length === 0) amountKeys.push('initial_balance');

  const runs: AdversarialRun[] = [];
  let runId = 0;

  // Insufficient: less than first HOLD requires
  const holdAmounts = graph.nodes
    .filter(n => n.nodeType === 'HOLD')
    .map(n => { const a = parseFloat(n.amount); return isNaN(a) ? 1000000 : a; })
    .filter(a => a > 0);

  if (holdAmounts.length > 0) {
    const minHold = Math.min(...holdAmounts);
    for (const key of amountKeys) {
      runs.push(makeRun(runId++, `Below HOLD: ${key} = ${minHold - 1}`, { ...base, [key]: String(Math.max(0, minHold - 1)) }, graph));
    }
  }

  // Partial funding: half of needed
  if (holdAmounts.length > 0) {
    const total = holdAmounts.reduce((s, a) => s + a, 0);
    for (const key of amountKeys) {
      runs.push(makeRun(runId++, `Half funding: ${key} = ${Math.floor(total / 2)}`, { ...base, [key]: String(Math.floor(total / 2)) }, graph));
    }
  }

  // Depleted: 0 for all
  const allZero = { ...base };
  for (const key of amountKeys) allZero[key] = '0';
  runs.push(makeRun(runId++, 'Depleted: all balances = 0', allZero, graph));

  // Single unit shortfall
  if (holdAmounts.length > 0) {
    const total = holdAmounts.reduce((s, a) => s + a, 0);
    for (const key of amountKeys) {
      runs.push(makeRun(runId++, `Off-by-one: ${key} = ${total - 1}`, { ...base, [key]: String(Math.max(0, total - 1)) }, graph));
    }
  }

  return runs;
}

function generateSplitIntegrityScenario(graph: PviGraphWithSpans): AdversarialRun[] {
  const base = getBaseInputs(graph);
  const runs: AdversarialRun[] = [];
  let runId = 0;

  // Look for split nodes
  const splitNodes = graph.nodes.filter(n => n.nodeType === 'SPLIT');
  if (splitNodes.length === 0) {
    // No splits — run base to verify no issues
    runs.push(makeRun(runId++, 'No splits — baseline', base, graph));
    return runs;
  }

  // Check actual split edge weights
  for (const splitNode of splitNodes) {
    const outEdges = graph.edges.filter(e => e.source === splitNode.id);
    const totalBp = outEdges.reduce((s, e) => s + e.weightBp, 0);

    if (totalBp !== 10000) {
      // Already an inconsistency — run to detect
      runs.push(makeRun(runId++, `Split #${splitNode.id}: weights sum to ${totalBp / 100}% (not 100%)`, base, graph));
    }
  }

  // Rounding test: amounts that don't divide evenly
  const oddAmounts = ['7', '13', '97', '10001', '33333'];
  const amountKeys = getAmountKeys(graph);
  for (const amt of oddAmounts) {
    const inputs = { ...base };
    for (const key of amountKeys) inputs[key] = amt;
    runs.push(makeRun(runId++, `Rounding test: amount = ${amt}`, inputs, graph));
  }

  // Very small amounts through splits
  const smallInputs = { ...base };
  for (const key of amountKeys) smallInputs[key] = '3';
  runs.push(makeRun(runId++, 'Small amount through split: 3', smallInputs, graph));

  return runs;
}

function generateConditionalLogicScenario(graph: PviGraphWithSpans): AdversarialRun[] {
  const base = getBaseInputs(graph);
  const runs: AdversarialRun[] = [];
  let runId = 0;

  // Find nodes with conditions
  const condNodes = graph.nodes.filter(n => n.conditions.length > 0);

  if (condNodes.length === 0) {
    runs.push(makeRun(runId++, 'No conditions — baseline', base, graph));
    return runs;
  }

  // Force all conditions to fail
  const failInputs = { ...base };
  for (const node of condNodes) {
    for (const cond of node.conditions) {
      if (cond.type === 'time_absolute') {
        failInputs['block_height'] = '0'; // Below any threshold
      }
    }
  }
  runs.push(makeRun(runId++, 'All conditions forced false', failInputs, graph));

  // Edge case: exactly at threshold
  for (const node of condNodes) {
    for (const cond of node.conditions) {
      if (cond.type === 'time_absolute') {
        const edgeInputs = { ...base, block_height: String(cond.after_height) };
        runs.push(makeRun(runId++, `Exact threshold: height = ${cond.after_height}`, edgeInputs, graph));

        const belowInputs = { ...base, block_height: String(cond.after_height - 1) };
        runs.push(makeRun(runId++, `Below threshold: height = ${cond.after_height - 1}`, belowInputs, graph));

        const aboveInputs = { ...base, block_height: String(cond.after_height + 1) };
        runs.push(makeRun(runId++, `Above threshold: height = ${cond.after_height + 1}`, aboveInputs, graph));
      }
      if (cond.type === 'oracle_threshold') {
        const key = `oracle_${cond.oracle_id}_${cond.field}`;
        const v = cond.value;

        const atInputs = { ...base, [key]: String(v) };
        runs.push(makeRun(runId++, `Oracle ${cond.oracle_id}.${cond.field} at threshold: ${v}`, atInputs, graph));

        const belowInputs = { ...base, [key]: String(v - 1) };
        runs.push(makeRun(runId++, `Oracle ${cond.oracle_id}.${cond.field} below threshold: ${v - 1}`, belowInputs, graph));

        const aboveInputs = { ...base, [key]: String(v + 1) };
        runs.push(makeRun(runId++, `Oracle ${cond.oracle_id}.${cond.field} above threshold: ${v + 1}`, aboveInputs, graph));
      }
    }
  }

  return runs;
}

function generateFlowIntegrityScenario(graph: PviGraphWithSpans): AdversarialRun[] {
  const base = getBaseInputs(graph);
  const runs: AdversarialRun[] = [];
  let runId = 0;

  // Static analysis: unreachable nodes
  const unreachable = findUnreachableNodes(graph);
  if (unreachable.length > 0) {
    const run = makeRun(runId++, `Unreachable nodes detected: ${unreachable.join(', ')}`, base, graph);
    for (const nid of unreachable) {
      run.failures.push({
        type: 'unreachable_execution',
        nodeId: nid,
        explanation: `Node #${nid} (${graph.nodes.find(n => n.id === nid)?.nodeType ?? 'unknown'}) is unreachable from any root node.`,
        suggestedFix: 'Add an edge from an upstream node to this node, or remove the orphan node.',
      });
    }
    runs.push(run);
  }

  // Static analysis: dangling HOLDs
  const dangling = findDanglingHolds(graph);
  if (dangling.length > 0) {
    const run = makeRun(runId++, `Dangling HOLDs: ${dangling.join(', ')}`, base, graph);
    for (const nid of dangling) {
      run.failures.push({
        type: 'incomplete_flow',
        nodeId: nid,
        explanation: `HOLD #${nid} has no outgoing edges — value is held but never released, refunded, or paid.`,
        suggestedFix: 'Add a release, refund, or pay operation downstream of this hold.',
      });
    }
    runs.push(run);
  }

  // Static analysis: missing refund paths
  const noRefund = findMissingRefundPaths(graph);
  if (noRefund.length > 0 && noRefund.length !== dangling.length) {
    const run = makeRun(runId++, `HOLDs without refund path: ${noRefund.join(', ')}`, base, graph);
    for (const nid of noRefund) {
      if (dangling.includes(nid)) continue; // Already reported above
      run.failures.push({
        type: 'incomplete_flow',
        nodeId: nid,
        explanation: `HOLD #${nid} has no path to a REFUND node — funds cannot be returned if conditions aren't met.`,
        suggestedFix: 'Add a conditional refund path for safety. Consider what happens if the release conditions are never met.',
      });
    }
    // Only add if there are unique failures
    if (run.failures.length > 0) runs.push(run);
  }

  // Cross-function vault lifecycle: detect multi-drain vaults
  const multiDrain = findMultiDrainVaults(graph);
  for (const md of multiDrain) {
    const holdNode = graph.nodes.find(n => n.id === md.holdId);
    const holdLabel = holdNode ? `HOLD #${md.holdId}` : `Node #${md.holdId}`;
    const drainLabels = md.drainNodeIds.map((did, i) => {
      return `${md.drainTypes[i]} #${did}`;
    });

    // Check if all drain nodes live in distinct functions (function-level isolation).
    // When drains are in separate entry-point functions, only one can execute per
    // call, so double-drain is impossible by construction.
    const drainFunctions = md.drainNodeIds.map(did => {
      const n = graph.nodes.find(n => n.id === did) as (typeof graph.nodes)[number] | undefined;
      return n?.functionName ?? null;
    });
    const allHaveFunction = drainFunctions.every(f => f !== null);
    const allDistinct = allHaveFunction && new Set(drainFunctions).size === drainFunctions.length;

    if (allDistinct) {
      // Drains are in separate functions — safe by function isolation.
      // Record as informational with no failures.
      runs.push({
        id: runId++,
        label: `Cross-function vault: ${holdLabel} — isolated by function entry points (${drainFunctions.join(', ')})`,
        inputs: base,
        result: runSimulation(graph, base),
        failures: [],
        executionTrace: [],
      });
      continue;
    }

    // Double-drain attack: both RELEASE and REFUND executing on same vault
    const hasRelease = md.drainTypes.includes('RELEASE');
    const hasRefund = md.drainTypes.includes('REFUND');
    if (hasRelease && hasRefund) {
      // Check if the drain nodes have mutually exclusive conditions
      const releaseIdx = md.drainTypes.indexOf('RELEASE');
      const refundIdx = md.drainTypes.indexOf('REFUND');
      const releaseNode = graph.nodes.find(n => n.id === md.drainNodeIds[releaseIdx]);
      const refundNode = graph.nodes.find(n => n.id === md.drainNodeIds[refundIdx]);

      if (releaseNode && refundNode && areMutuallyExclusive(releaseNode.conditions, refundNode.conditions)) {
        // Conditions are complementary — this is a safe pattern (e.g. require price > X / require price <= X)
        // The simulation runs both paths sequentially (which can't happen in practice),
        // so we discard simulation-level failures from the safe run.
        runs.push({
          id: runId++,
          label: `Cross-function vault: ${holdLabel} — mutually exclusive conditions`,
          inputs: base,
          result: runSimulation(graph, base),
          failures: [],
          executionTrace: [],
        });
      } else {
        const doubleDrainRun = makeRun(
          runId++,
          `Double-drain attack: ${holdLabel} → ${drainLabels.join(' + ')}`,
          base,
          graph,
        );
        doubleDrainRun.failures.push({
          type: 'invalid_flow',
          nodeId: md.holdId,
          explanation: `${holdLabel} has both release (${drainLabels.filter((_, i) => md.drainTypes[i] === 'RELEASE').join(', ')}) and refund (${drainLabels.filter((_, i) => md.drainTypes[i] === 'REFUND').join(', ')}) paths. If both execute in the same invocation, the second drain will find an empty escrow. Ensure conditions or separate function entry points prevent double-drain.`,
          suggestedFix: 'Add mutually exclusive conditions (e.g. time lock or oracle guard) so only one drain path executes per call, or use separate entry-point functions.',
        });
        runs.push(doubleDrainRun);
      }
    }

    // Cross-function isolation: test each drain path independently
    for (let i = 0; i < md.drainNodeIds.length; i++) {
      const drainId = md.drainNodeIds[i];
      const drainType = md.drainTypes[i];
      // Build path-specific inputs that satisfy this drain node's conditions
      const pathInputs = { ...base };
      const drainNode = graph.nodes.find(n => n.id === drainId);
      if (drainNode) {
        for (const cond of drainNode.conditions) {
          if (cond.type === 'oracle_threshold') {
            const key = `oracle_${cond.oracle_id}_${cond.field}`;
            pathInputs[key] = String(oracleDefaultForOp(cond.operator, cond.value));
          }
          if (cond.type === 'time_absolute') {
            pathInputs['block_height'] = String(cond.after_height + 1);
          }
        }
      }
      const pathRun = makeRun(
        runId++,
        `Cross-function path: ${holdLabel} → ${drainType} #${drainId}`,
        pathInputs,
        graph,
      );
      // Check if this drain actually moved value
      const drainStep = pathRun.result.steps.find(s => s.nodeId === drainId);
      if (drainStep && drainStep.changes.length === 0 && drainStep.status !== 'error') {
        pathRun.failures.push({
          type: 'incomplete_flow',
          nodeId: drainId,
          explanation: `${drainType} #${drainId} completed but moved no value — escrow may have been drained by an earlier operation in the same execution.`,
          suggestedFix: `Ensure ${drainType.toLowerCase()} is called from a separate entry point so the vault still holds value when it executes.`,
        });
      }
      runs.push(pathRun);
    }
  }

  // Run baseline and check for locked value
  const baseRun = makeRun(runId++, 'Flow integrity baseline', base, graph);
  const locked = checkLockedValue(baseRun.result);
  if (locked > 0) {
    baseRun.failures.push({
      type: 'incomplete_flow',
      nodeId: null,
      explanation: `${locked} units of value remain locked in escrow after execution. This value is inaccessible.`,
      suggestedFix: 'Ensure all held value is eventually released, refunded, or burned.',
    });
  }
  runs.push(baseRun);

  return runs;
}

function generateMultiStepStressScenario(graph: PviGraphWithSpans): AdversarialRun[] {
  const base = getBaseInputs(graph);
  const runs: AdversarialRun[] = [];
  let runId = 0;

  // Run 1: Standard execution
  runs.push(makeRun(runId++, 'Baseline execution', base, graph));

  // Run 2: Repeated execution (simulate idempotency)
  // Run the same graph twice — should produce same result
  const r1 = runSimulation(graph, base);
  const r2 = runSimulation(graph, base);
  const repeatRun: AdversarialRun = {
    id: runId++,
    label: 'Repeated execution (idempotency)',
    inputs: base,
    result: r2,
    failures: [],
    executionTrace: r2.steps,
  };
  if (r1.success !== r2.success || r1.totalValueMoved !== r2.totalValueMoved) {
    repeatRun.failures.push({
      type: 'value_mismatch',
      nodeId: null,
      explanation: 'Non-deterministic behavior detected: repeated execution produced different results.',
      suggestedFix: 'Ensure contract logic is fully deterministic with no external state dependencies.',
    });
  }
  runs.push(repeatRun);

  // Run 3-5: Gradually increasing amounts
  const scales = [10, 100, 1000];
  const amountKeys = getAmountKeys(graph);
  for (const scale of scales) {
    const scaledInputs = { ...base };
    for (const key of amountKeys) {
      const orig = parseInt(base[key] ?? '1000000', 10);
      scaledInputs[key] = String(orig * scale);
    }
    runs.push(makeRun(runId++, `Scale ${scale}x amounts`, scaledInputs, graph));
  }

  return runs;
}

/* ================================================================== */
/*  Run Executor                                                      */
/* ================================================================== */

function makeRun(
  id: number,
  label: string,
  inputs: SimulationInputValues,
  graph: PviGraphWithSpans,
): AdversarialRun {
  const result = runSimulation(graph, inputs);
  const failures = classifyFailures(result, graph);

  return {
    id,
    label,
    inputs,
    result,
    failures,
    executionTrace: result.steps,
  };
}

/* ================================================================== */
/*  Failure Classifier                                                */
/* ================================================================== */

function classifyFailures(
  result: SimulationResult,
  graph: PviGraphWithSpans,
): AdversarialFailure[] {
  const failures: AdversarialFailure[] = [];

  // Collect nodes that completed without error — used to detect
  // mutually exclusive condition pairs that are expected to have one side fail.
  const succeededNodes = result.steps
    .filter(s => s.status !== 'error')
    .map(s => graph.nodes.find(n => n.id === s.nodeId))
    .filter((n): n is NonNullable<typeof n> => !!n);

  for (const step of result.steps) {
    if (step.status !== 'error' || !step.error) continue;

    const node = graph.nodes.find(n => n.id === step.nodeId);
    const nodeLabel = node ? `${node.nodeType} #${node.id}` : `Node #${step.nodeId}`;

    // If this node failed due to an unmet condition, and a *succeeded* node has
    // mutually exclusive conditions (complementary oracle/time guard), this is
    // expected — the contract is structured so exactly one path executes.
    const isConditionError =
      step.error.includes('Block height') ||
      step.error.includes('Oracle ') ||
      step.error.includes('does not satisfy');

    if (isConditionError && node && node.conditions.length > 0) {
      const hasMutuallyExclusiveSibling = succeededNodes.some(
        sn => sn.id !== node.id && areMutuallyExclusive(node.conditions, sn.conditions),
      );
      if (hasMutuallyExclusiveSibling) continue; // expected failure — skip
    }

    if (step.error.includes('Insufficient balance')) {
      failures.push({
        type: 'insufficient_balance',
        nodeId: step.nodeId,
        explanation: `${nodeLabel}: ${step.error}`,
        suggestedFix: 'Increase the initial balance for the sender, or reduce the operation amount.',
      });
    } else if (step.error.includes('Insufficient escrow')) {
      failures.push({
        type: 'insufficient_balance',
        nodeId: step.nodeId,
        explanation: `${nodeLabel}: ${step.error}`,
        suggestedFix: 'Ensure upstream HOLD operations deposit sufficient value into escrow before this PAY.',
      });
    } else if (isConditionError) {
      failures.push({
        type: 'failed_condition',
        nodeId: step.nodeId,
        explanation: `${nodeLabel}: ${step.error}`,
        suggestedFix: 'Ensure the condition (oracle threshold or block height) is met before this operation executes.',
      });
    } else {
      failures.push({
        type: 'invalid_flow',
        nodeId: step.nodeId,
        explanation: `${nodeLabel}: ${step.error}`,
        suggestedFix: null,
      });
    }
  }

  // Check for value mismatch (non-MINT/BURN graphs should conserve value)
  const hasMintOrBurn = graph.nodes.some(n => n.nodeType === 'MINT' || n.nodeType === 'BURN');
  if (!hasMintOrBurn && result.success) {
    const totalInitial = sumAllBalances(result.steps[0]?.balancesBefore ?? {});
    const totalFinal = sumAllBalances(result.finalBalances);
    if (totalInitial > 0 && Math.abs(totalFinal - totalInitial) > 0) {
      failures.push({
        type: 'value_mismatch',
        nodeId: null,
        explanation: `Value changed from ${totalInitial} to ${totalFinal} without MINT or BURN. ${totalFinal - totalInitial} units unaccounted.`,
        suggestedFix: 'Check for rounding errors in split operations or missing escrow returns.',
      });
    }
  }

  // Check for locked value in escrow
  const locked = checkLockedValue(result);
  if (locked > 0 && result.success) {
    failures.push({
      type: 'incomplete_flow',
      nodeId: null,
      explanation: `${locked} units remain locked in escrow after successful execution.`,
      suggestedFix: 'Ensure all held value is eventually released, refunded, or burned.',
    });
  }

  // Check split warnings
  for (const warn of result.warnings) {
    if (warn.includes('Split percentages')) {
      const match = warn.match(/Node #(\d+)/);
      failures.push({
        type: 'split_inconsistency',
        nodeId: match ? parseInt(match[1], 10) : null,
        explanation: warn,
        suggestedFix: 'Ensure all split edges sum to exactly 100% (10000 basis points).',
      });
    }
  }

  return failures;
}

function sumAllBalances(balances: { [addr: string]: { [asset: string]: number } }): number {
  let total = 0;
  for (const assets of Object.values(balances)) {
    for (const v of Object.values(assets)) {
      total += v;
    }
  }
  return total;
}

/* ================================================================== */
/*  Main Engine Entry Point                                           */
/* ================================================================== */

/**
 * Run all adversarial scenarios for a given graph.
 * Each scenario generates multiple deterministic runs.
 */
export function runAdversarialAnalysis(
  graph: PviGraphWithSpans,
  categories?: ScenarioCategory[],
  maxRunsPerScenario = 20,
): AdversarialResult {
  const activeCategories = categories ?? SCENARIO_CATEGORIES.map(c => c.id);

  const scenarios: ScenarioResult[] = [];
  const failedNodeIds = new Set<NodeId>();
  const warningNodeIds = new Set<NodeId>();
  const allNodeIds = new Set<NodeId>(graph.nodes.map(n => n.id));
  let totalRuns = 0;
  let totalFailures = 0;
  let totalWarnings = 0;

  const generators: Record<ScenarioCategory, (g: PviGraphWithSpans) => AdversarialRun[]> = {
    boundary: generateBoundaryScenario,
    balance_attack: generateBalanceAttackScenario,
    split_integrity: generateSplitIntegrityScenario,
    conditional_logic: generateConditionalLogicScenario,
    flow_integrity: generateFlowIntegrityScenario,
    multi_step_stress: generateMultiStepStressScenario,
  };

  for (const catId of activeCategories) {
    const catInfo = SCENARIO_CATEGORIES.find(c => c.id === catId);
    if (!catInfo) continue;

    const generator = generators[catId];
    let runs = generator(graph);

    // Limit runs
    if (runs.length > maxRunsPerScenario) {
      runs = runs.slice(0, maxRunsPerScenario);
    }

    let catFailures = 0;
    let catWarnings = 0;

    for (const run of runs) {
      catFailures += run.failures.length;
      catWarnings += run.result.warnings.length;
      for (const f of run.failures) {
        if (f.nodeId !== null) failedNodeIds.add(f.nodeId);
      }
      for (const w of run.result.warnings) {
        const match = w.match(/Node #(\d+)/);
        if (match) warningNodeIds.add(parseInt(match[1], 10));
      }
    }

    scenarios.push({
      category: catId,
      label: catInfo.label,
      runs,
      totalRuns: runs.length,
      totalFailures: catFailures,
      totalWarnings: catWarnings,
    });

    totalRuns += runs.length;
    totalFailures += catFailures;
    totalWarnings += catWarnings;
  }

  const passedNodeIds = new Set<NodeId>();
  for (const nid of allNodeIds) {
    if (!failedNodeIds.has(nid) && !warningNodeIds.has(nid)) {
      passedNodeIds.add(nid);
    }
  }

  return {
    scenarios,
    totalRuns,
    totalFailures,
    totalWarnings,
    failedNodeIds,
    warningNodeIds,
    passedNodeIds,
    timestamp: Date.now(),
  };
}

/**
 * Run a single adversarial scenario category.
 */
export function runSingleScenario(
  graph: PviGraphWithSpans,
  category: ScenarioCategory,
  maxRuns = 20,
): ScenarioResult {
  const result = runAdversarialAnalysis(graph, [category], maxRuns);
  return result.scenarios[0];
}
